import { BillingModel, BillingModelConfig } from './types';

/**
 * Configuration for each billing model.
 *
 * Packages: Traditional model - clients buy session bundles (e.g., 10 sessions)
 * Subscription: Monthly recurring membership
 * Pay-per-session: Client pays for each individual session
 * No payment: Trainer handles payments outside the app
 */
export const BILLING_MODEL_CONFIGS: Record<BillingModel, BillingModelConfig> = {
  packages: {
    id: 'packages',
    label: 'Session Packages',
    description: 'Clients purchase session bundles (e.g., 10 sessions) and use them over time',
    features: {
      showPackages: true,
      showSessionsRemaining: true,
      showReupProjections: true,
      trackEarningsPerSession: true,
      requirePackageForSession: true,
      showRevenueForecast: true,
    },
    churnRiskFactors: {
      packageEndingSoon: true,
      noRenewalScheduled: true,
      subscriptionEndingSoon: false,
      paymentOverdue: false,
    },
  },

  subscription: {
    id: 'subscription',
    label: 'Monthly Subscription',
    description: 'Clients pay a recurring monthly fee for unlimited or set number of sessions',
    features: {
      showPackages: false,
      showSessionsRemaining: false,
      showReupProjections: false,
      trackEarningsPerSession: false,
      requirePackageForSession: false,
      showRevenueForecast: true,
    },
    churnRiskFactors: {
      packageEndingSoon: false,
      noRenewalScheduled: false,
      subscriptionEndingSoon: true,
      paymentOverdue: false,
    },
  },

  pay_per_session: {
    id: 'pay_per_session',
    label: 'Pay Per Session',
    description: 'Clients pay for each session individually, no packages or subscriptions',
    features: {
      showPackages: false,
      showSessionsRemaining: false,
      showReupProjections: false,
      trackEarningsPerSession: true,
      requirePackageForSession: false,
      showRevenueForecast: true,
    },
    churnRiskFactors: {
      packageEndingSoon: false,
      noRenewalScheduled: false,
      subscriptionEndingSoon: false,
      paymentOverdue: true,
    },
  },

  no_payment: {
    id: 'no_payment',
    label: 'No Payment Tracking',
    description: 'Handle payments outside the app - focus only on sessions and client progress',
    features: {
      showPackages: false,
      showSessionsRemaining: false,
      showReupProjections: false,
      trackEarningsPerSession: false,
      requirePackageForSession: false,
      showRevenueForecast: false,
    },
    churnRiskFactors: {
      packageEndingSoon: false,
      noRenewalScheduled: false,
      subscriptionEndingSoon: false,
      paymentOverdue: false,
    },
  },
};

/**
 * Default billing model for new organizations and backwards compatibility.
 */
export const DEFAULT_BILLING_MODEL: BillingModel = 'packages';

/**
 * All billing models in display order for selectors.
 */
export const BILLING_MODEL_OPTIONS: BillingModel[] = [
  'packages',
  'subscription',
  'pay_per_session',
  'no_payment',
];
